import React, { Fragment } from 'react';
import { shape, bool, func, object } from 'prop-types';
import { connect } from 'react-redux';
import { Input, Button } from '@noths/shared-components';
import FullPageSpinner from '../../components/FullPageSpinner';
import Types from '../../redux/types';
import { isValidPasswordLength, isValidEmailFormat } from '../Register/validations/validations';
import ErrorPanel from '../../components/ErrorPanel';
import MarketingModal from '../../components/Modal';
import { trackRegisterClick, trackForgotPasswordClick } from '../../analytics';
import { ROUTES, SIGNED_OUT_QUERY_PARAM } from '../../constants';
import { errorPropType } from '../../prop-types';
import { Container, ForgotPasswordContainer, ButtonContainer } from './styles';
import * as Form from '../../components/V2/Form';
import Link from '../../components/V2/Link';
import Paragraph from '../../components/V2/Paragraph';
import InfoPanel from '../../components/InfoPanel';
import Info from '../../images/info.svg';

export class SignIn extends React.Component {
  static propTypes = {
    location: object,
    signIn: shape({
      error: errorPropType,
      loading: bool.isRequired,
    }).isRequired,
    // continueWithFacebook: func.isRequired,
    handleSignInSubmit: func.isRequired,
    handleModalClose: func.isRequired,
    handleEmailChange: func.isRequired,
    handleShowPage: func.isRequired,
  };

  static defaultProps = {
    location: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      emptyEmail: false,
      invalidEmail: false,
      emptyPassword: false,
      passwordTooShort: false,
      email: '',
      password: '',
    };

    props.handleShowPage(props.location);
  }

  getEmailErrorMessage = () => {
    const { invalidEmail, emptyEmail } = this.state;
    if (invalidEmail) return 'Sorry your email is invalid';
    if (emptyEmail) return 'Please enter an email';
    return '';
  };

  getPasswordErrorMessage = () => {
    const { passwordTooShort, emptyPassword } = this.state;
    if (passwordTooShort) return 'Password is too short';
    if (emptyPassword) return 'Please enter your password';
    return '';
  };

  getQueryParamsToRemember = () => {
    const { search } = this.props.location;
    return search && !this.hasSignedOut() ? search : '';
  };

  hasSignedOut = () => {
    const { search } = this.props.location;
    return search === `?${SIGNED_OUT_QUERY_PARAM}`;
  };

  handleEmailInputChange = e => {
    const { value } = e.target;
    this.setState(() => ({
      email: value,
      emptyEmail: false,
      invalidEmail: false,
    }));
    this.props.handleEmailChange();
  };

  handleForgotPasswordClick = () => {
    trackForgotPasswordClick(ROUTES.FORGOT_PASSWORD);
  };

  handlePasswordInputChange = e => {
    const { value } = e.target;
    this.setState({
      password: value,
      emptyPassword: false,
      passwordTooShort: false,
    });
  };

  handleSubmit = e => {
    e.preventDefault();
    const { email, password } = this.state;
    this.setState(() => ({ password: '' }));
    if (email.length === 0) {
      this.setState({ emptyEmail: true });
    } else if (!isValidEmailFormat(email)) {
      this.setState({ invalidEmail: true });
    } else if (password.length === 0) {
      this.setState({ emptyPassword: true });
    } else if (!isValidPasswordLength(password)) {
      this.setState({ passwordTooShort: true });
    } else {
      this.props.handleSignInSubmit(email, password);
    }
  };

  handleEmailBlur = () => {
    const { email } = this.state;
    if (email.length === 0) {
      this.setState({ emptyEmail: true });
    } else if (!isValidEmailFormat(email)) {
      this.setState({ invalidEmail: true });
    }
  };

  handlePasswordBlur = () => {
    const { password } = this.state;
    if (password.length === 0) {
      this.setState({ emptyPassword: true });
    } else if (password.length < 8) {
      this.setState({ passwordTooShort: true });
    }
  };

  handleRegisterAnalytics = url => () => {
    trackRegisterClick(url);
  };

  render() {
    const { error } = this.props.signIn;
    const queryParams = this.getQueryParamsToRemember();

    return (
      <Fragment>
        {this.props.signIn.loading && <FullPageSpinner />}
        <MarketingModal
          showModal={this.props.signIn.showModal}
          toggleModal={this.props.handleModalClose}
        />
        {/*
          Disabling Facebook button until a feature toggle is in place
          <button onClick={() => this.props.continueWithFacebook()}>Continue with Facebook</button>
        */}
        <Container>
          {error && <ErrorPanel error={error} />}
          {this.hasSignedOut() && (
            <InfoPanel type="information" message="You have been signed out">
              <img src={Info} alt="" />
            </InfoPanel>
          )}
          <Form.Container onSubmit={this.handleSubmit} method="post">
            <Form.Header>Sign in</Form.Header>
            <Paragraph textAlign="center" secondary>
              Don't have an account?{' '}
              <Link
                onMouseDown={this.handleRegisterAnalytics(`${ROUTES.REGISTER}${queryParams}`)}
                href={`${ROUTES.REGISTER}${queryParams}`}
              >
                Register now
              </Link>
            </Paragraph>
            <Form.InputContainer>
              <Input
                id="email"
                name="email"
                type="email"
                size="small"
                label="Email address"
                autoComplete="email"
                placeholder="janedoe@email.com"
                errorMessage={this.getEmailErrorMessage()}
                onBlur={this.handleEmailBlur}
                onChange={this.handleEmailInputChange}
                value={this.state.email}
                data-email-field
              />
            </Form.InputContainer>
            <Form.InputContainer>
              <Input
                id="password"
                name="password"
                size="small"
                label="Password"
                type="password"
                autoComplete="current-password"
                errorMessage={this.getPasswordErrorMessage()}
                onBlur={this.handlePasswordBlur}
                onChange={this.handlePasswordInputChange}
                value={this.state.password}
                data-password-field
              />
            </Form.InputContainer>
            <ForgotPasswordContainer>
              <Link
                href={ROUTES.FORGOT_PASSWORD}
                onMouseDown={this.handleForgotPasswordClick}
                data-forgot-password-button
              >
                forgotten password?
              </Link>
            </ForgotPasswordContainer>
            <ButtonContainer>
              <Button
                type="submit"
                size="medium"
                width="100%"
                data-sign-in-button
                disabled={this.props.signIn.loading}
              >
                Sign in
              </Button>
            </ButtonContainer>
          </Form.Container>
        </Container>
      </Fragment>
    );
  }
}

const mapStateToProps = ({ signIn, router }) => ({
  signIn,
  location: router.location,
});

export const mapDispatchToProps = dispatch => ({
  continueWithFacebook: () => dispatch({ type: Types.CONTINUE_WITH_FACEBOOK }),
  handleShowPage: location => dispatch({ type: Types.SIGN_IN_SHOW, location }),
  handleSignInSubmit: (email, password) => {
    dispatch({
      type: Types.SIGN_IN_SUBMIT,
      email,
      password,
    });
  },
  handleEmailChange: () => {
    dispatch({
      type: Types.SIGN_IN_EMAIL_CHANGE,
    });
  },
  handleModalClose: () => {
    dispatch({
      type: Types.SIGN_IN_MODAL_CLOSE,
    });
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SignIn);
